import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { memo, useMemo } from "react";
import "react-json-view-lite/dist/index.css";
import { JsonView, collapseAllNested, darkStyles, defaultStyles } from "react-json-view-lite";

type JsonViewData = Record<string, unknown> | unknown[];

function tryParseJsonString(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function isJsonViewObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toJsonViewData(value: unknown): JsonViewData {
  const parsedValue = typeof value === "string" ? tryParseJsonString(value) : value;

  if (Array.isArray(parsedValue)) {
    return parsedValue;
  }

  if (isJsonViewObject(parsedValue)) {
    return parsedValue;
  }

  return { value: parsedValue };
}

export const ToolIoJsonBlock = memo(function ToolIoJsonBlock(props: {
  title: string;
  value: unknown;
}) {
  const theme = useTheme();
  const data = useMemo(() => toJsonViewData(props.value), [props.value]);
  const baseStyles = theme.palette.mode === "dark" ? darkStyles : defaultStyles;
  const encampBlue = theme.palette.encampBlue ?? theme.palette.primary;
  const encampGreen = theme.palette.encampGreen ?? theme.palette.success;
  const scoutPanelBackground = theme.palette.grey50?.main ?? theme.palette.action.hover;
  const jsonViewStyles = useMemo(
    () => ({
      ...baseStyles,
      container: `${baseStyles.container} scout-tool-json`,
      basicChildStyle: `${baseStyles.basicChildStyle} scout-tool-json-row`,
      label: `${baseStyles.label} scout-tool-json-label`,
      clickableLabel: `${baseStyles.clickableLabel} scout-tool-json-label`,
      nullValue: `${baseStyles.nullValue} scout-tool-json-null`,
      undefinedValue: `${baseStyles.undefinedValue} scout-tool-json-null`,
      numberValue: `${baseStyles.numberValue} scout-tool-json-number`,
      stringValue: `${baseStyles.stringValue} scout-tool-json-string`,
      booleanValue: `${baseStyles.booleanValue} scout-tool-json-boolean`,
      otherValue: `${baseStyles.otherValue} scout-tool-json-other`,
      punctuation: `${baseStyles.punctuation} scout-tool-json-punctuation`,
      expandIcon: `${baseStyles.expandIcon} scout-tool-json-icon`,
      collapseIcon: `${baseStyles.collapseIcon} scout-tool-json-icon`,
      collapsedContent: `${baseStyles.collapsedContent} scout-tool-json-collapsed`,
      childFieldsContainer: `${baseStyles.childFieldsContainer} scout-tool-json-children`,
    }),
    [baseStyles]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        color="text.secondary"
        variant="caption"
        sx={{ display: "block", fontWeight: 600, letterSpacing: 0.2, mb: 0.5 }}
      >
        {props.title}
      </Typography>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1.5,
          bgcolor: theme.palette.mode === "dark" ? "background.paper" : scoutPanelBackground,
          color: "text.primary",
          overflow: "hidden",
          wordBreak: "break-word",
          "& .scout-tool-json": {
            maxHeight: 360,
            overflow: "auto",
            bgcolor: "background.paper",
            fontFamily: "monospace",
            fontSize: "0.75rem",
            lineHeight: 1.45,
            p: 1.25,
          },
          "& .scout-tool-json-row": {
            px: 0,
            py: 0.125,
          },
          "& .scout-tool-json-children": {
            borderLeft: 1,
            borderColor: "divider",
            ml: 1.25,
            pl: 1.25,
          },
          "& .scout-tool-json-label": {
            color: "text.primary",
            fontWeight: 700,
            mr: 0.5,
          },
          "& .scout-tool-json-string": {
            color: theme.palette.mode === "dark" ? encampGreen.light : encampGreen.dark,
          },
          "& .scout-tool-json-number": {
            color: theme.palette.mode === "dark" ? encampBlue.light : encampBlue.dark,
            fontWeight: 600,
          },
          "& .scout-tool-json-boolean, & .scout-tool-json-null, & .scout-tool-json-other": {
            color:
              theme.palette.mode === "dark"
                ? theme.palette.secondary.light
                : theme.palette.secondary.dark,
            fontWeight: 600,
          },
          "& .scout-tool-json-punctuation": {
            color: "text.secondary",
            fontWeight: 600,
          },
          "& .scout-tool-json-icon": {
            color: "text.secondary",
            mr: 0.75,
          },
          "& .scout-tool-json-collapsed": {
            color: "text.disabled",
          },
          "& .scout-tool-json-label:hover, & .scout-tool-json-icon:hover": {
            color: "primary.main",
          },
          "& .scout-tool-json::selection, & .scout-tool-json *::selection": {
            bgcolor: alpha(encampBlue.main, 0.18),
          },
        }}
      >
        <JsonView
          aria-label={props.title}
          data={data}
          shouldExpandNode={collapseAllNested}
          style={jsonViewStyles}
        />
      </Box>
    </Box>
  );
});
